/*
 *  Conveyor + Robot 3D layout component
 *  USAGE:
 *  ---------------------------
 *  1) create:
 *      in template:
 *      ...
 *      <div id="conveyor3d"></div>
 *      ...
 *
 *      in javascript:
 *      ...
 *      view = new Conveyor3D('conveyor3d', ConveyorConfigurationEnum.LEFT, null)
 *      ...
 *
 *  2) set robot postition:
 *      view.setPosition('LEFT')
 *      view.render()
 *
 *  3) set label position:
 *      view.setLabel('FRONT')
 *
 *  4) set box orientation:
 *      view.setBoxOrient('SHORT_SIDE_LEADING')
 *
 */

import { Box, ConveyorConfigurationEnum, ProgramSettings } from '../generated/model/models'
import {Box2} from './box2'
import * as THREE from 'three'
import {basePath} from '@/main'
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls'

const defWidth = 900
const defHeight = 900
const conveyorWidth = 400
const conveyorLength = 900

export interface ViewPort {
  h: number,
  w: number
}

export interface Position {
  id: ConveyorConfigurationEnum
  robot: {x: number, y: number}
  box: {x: number, ky: number, rot: number}
  palletA: {x: number, y: number, rot: number}
  palletB: {x: number, y: number, rot: number},
  ref: {y: number}
}

const positions: Position[] = [
  {
    id: 'CENTER_LEFT',
    robot: {x: -150, y: 0},
    box: {x: 210, ky: 1, rot: 0},
    palletA: {x: -850, y: 300, rot: 0},
    palletB: {x: -850, y: -650, rot: 0},
    ref: { y: 439 }
  },
  {
    id: 'CENTER_RIGHT',
    robot: {x: -150, y: 0},
    box: {x: 210, ky: 0, rot: 0 },
    palletA: {x: -850, y: 300, rot: 0},
    palletB: {x: -850, y: -650, rot: 0},
    ref: { y: 40 }
  },
  {
    id: 'LEFT',
    robot: {x: 200, y: 550},
    box: {x: 210, ky: 1, rot: 0 },
    palletA: {x: -50, y: 550, rot: Math.PI / 2},
    palletB: {x: 850, y: 550, rot: Math.PI / 2},
    ref: { y: 0 }
  },
  {
    id: 'RIGHT',
    robot: {x: 200, y: -400},
    box: {x: 210, ky: 0, rot: 0 },
    palletA: {x: -50, y: -850, rot: Math.PI / 2},
    palletB: {x: 850, y: -850, rot: Math.PI / 2},
    ref: { y: 0 }
  }
]

export class Conveyor3D {
  private viewPort: ViewPort = { h: 700, w: 860}
  private background: number = 0xfafafa
  private box: Box2
  private container: HTMLElement
  private scene: THREE.Scene
  private camera: THREE.OrthographicCamera
  private renderer: THREE.WebGLRenderer
  private boxMaterials: THREE.Material[]
  private controls: OrbitControls
  private group: THREE.Group
  private robotObject?: THREE.Object3D
  private boxObject: THREE.Object3D
  private boxGroup: THREE.Group
  private palletA_Object: THREE.Group
  private palletB_Object: THREE.Group
  private labelA: THREE.Object3D
  private labelB: THREE.Object3D
  private angle: number = 0
  private animation: boolean = false

  private robotPos: ConveyorConfigurationEnum = ConveyorConfigurationEnum.LEFT
  private robotPosConfig: Position = positions[2]
  private labelPos: ProgramSettings.LabelPositionEnum = ProgramSettings.LabelPositionEnum.RIGHT
  private isCustom: boolean = false
  private refGroup?: THREE.Group
  private font?: THREE.Font

  constructor (elementID: string,
               robotPos: ConveyorConfigurationEnum,
               afterCreated?: () => void,
               viewPort?: ViewPort,
               box?: Box,
               background?: number) {
    this.background = background ? background : this.background
    this.box = new Box2(box ? box : { length: 300, width: 200, height: 170 })
    this.viewPort = viewPort ? viewPort : this.viewPort
    this.robotPos = robotPos

    const element = document.getElementById(elementID)
    if (!element) {
      console.log(`[ERROR] html element '${elementID}' not found`)
    }
    this.container = element ? element : document.createElement(elementID)
    this.scene = this.sceneSetup()
    this.camera = this.cameraSetup()
    this.lightSetup()
    this.group = new THREE.Group()
    this.group.position.set(-100, -600, 600)
    this.scene.add(this.group)

    this.boxMaterials = this.boxSetup()
    this.renderer = this.renderSetup()
    this.controls = this.controlsSetup()

    this.makeReferenceFrame()
    this.makeConveyor()

    this.labelA = this.makeLabel()
    this.labelB = this.makeLabel()
    this.boxMaterials = this.boxSetup()
    const {obj, group} = this.makeBox(this.box, this.boxMaterials)
    this.boxObject = obj
    this.boxGroup = group
    this.setLabel(ProgramSettings.LabelPositionEnum.FRONT_RIGHT_CORNER)

    this.palletA_Object = this.makePallet()
    this.palletB_Object = this.makePallet()
    this.importRobot(afterCreated)
  }

  render () {
    this.renderer.render(this.scene, this.camera)
  }

  setPosition(position: ConveyorConfigurationEnum) {
    // console.log('[DEBUG] set position', position, this.isCustom)
    this.robotPos = position
    const found = positions.find(p => p.id === position)
    const pos = found ? found : positions[2]
    this.robotPosConfig = pos

    // place robot
    if (this.robotObject) {
      this.robotObject.position.x = pos.robot.x
      this.robotObject.position.y = pos.robot.y
    }

    // place box
    this.setBoxPosition(pos)
    // place reference
    this.setRefPosition(pos)

    // place pallets
    const palletA = this.palletA_Object
    const palletB = this.palletB_Object
    if (palletA && palletB) {
      palletA.position.x = pos.palletA.x
      palletA.position.y = pos.palletA.y
      palletA.rotation.z = pos.palletA.rot
      palletB.position.x = pos.palletB.x
      palletB.position.y = pos.palletB.y
      palletB.rotation.z = pos.palletB.rot
    }

    this.render()
  }

  setLabel (pos: ProgramSettings.LabelPositionEnum) {
    this.labelPos = pos
    const length = 100
    const frontLength = this.box.frontLength
    const sideLength = this.box.sideLength

    const front = (label: THREE.Object3D) => {
      label.rotation.x = 0
      label.rotation.y = -Math.PI / 2
      label.rotation.z = Math.PI / 2
      label.position.x = -(sideLength / 2 + 1)
      label.position.y = 0
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    const back = (label: THREE.Object3D) => {
      label.rotation.x = 0
      label.rotation.y = Math.PI / 2
      label.rotation.z = Math.PI / 2
      label.position.x = +(sideLength / 2 + 1)
      label.position.y = 0
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    const left = (label: THREE.Object3D) => {
      label.rotation.x = Math.PI / 2
      label.rotation.y = 0
      label.rotation.z = 0
      label.position.x = 0
      label.position.y = -(frontLength / 2 + 1)
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    const right = (label: THREE.Object3D) => {
      label.rotation.x = -Math.PI / 2
      label.rotation.y = 0
      label.rotation.z = 0
      label.position.x = 0
      label.position.y = +(frontLength / 2 + 1)
      label.position.z = this.box.height / 2 - 65 / 2 - 10
    }

    let visibleLabelPos = this.labelPos
    if (this.isCustom) {
      switch (visibleLabelPos) {
        case ProgramSettings.LabelPositionEnum.RIGHT:
          visibleLabelPos = ProgramSettings.LabelPositionEnum.LEFT
          break
        case ProgramSettings.LabelPositionEnum.LEFT:
          visibleLabelPos = ProgramSettings.LabelPositionEnum.RIGHT
          break
        case ProgramSettings.LabelPositionEnum.FRONT_RIGHT_CORNER:
          visibleLabelPos = ProgramSettings.LabelPositionEnum.LEFT_FRONT_CORNER
          break
        case ProgramSettings.LabelPositionEnum.LEFT_FRONT_CORNER:
          visibleLabelPos = ProgramSettings.LabelPositionEnum.FRONT_RIGHT_CORNER
          break
        case ProgramSettings.LabelPositionEnum.RIGHT_BACK_CORNER:
          visibleLabelPos = ProgramSettings.LabelPositionEnum.BACK_LEFT_CORNER
          break
        case ProgramSettings.LabelPositionEnum.BACK_LEFT_CORNER:
          visibleLabelPos = ProgramSettings.LabelPositionEnum.RIGHT_BACK_CORNER
          break
      }
    }

    switch (visibleLabelPos) {
      case ProgramSettings.LabelPositionEnum.FRONT:
      case ProgramSettings.LabelPositionEnum.IGNORE:
        this.boxGroup.remove(this.labelB)
        front(this.labelA)
        break

      case ProgramSettings.LabelPositionEnum.RIGHT:
        this.boxGroup.remove(this.labelB)
        right(this.labelA)
        break

      case ProgramSettings.LabelPositionEnum.FRONT_RIGHT_CORNER:
        this.boxGroup.remove(this.labelB)
        this.boxGroup.add(this.labelB)
        right(this.labelA)
        front(this.labelB)
        this.labelA.position.x = -(sideLength / 2 - length / 2)
        this.labelB.position.y = +(frontLength / 2 - length / 2)
        break

      case ProgramSettings.LabelPositionEnum.LEFT:
        this.boxGroup.remove(this.labelB)
        left(this.labelA)
        break

      case ProgramSettings.LabelPositionEnum.BACK:
        this.boxGroup.remove(this.labelB)
        back(this.labelA)
        break

      case ProgramSettings.LabelPositionEnum.RIGHT_BACK_CORNER:
        this.boxGroup.remove(this.labelB)
        this.boxGroup.add(this.labelB)
        right(this.labelA)
        back(this.labelB)
        this.labelA.position.x = +(sideLength / 2 - length / 2)
        this.labelB.position.y = +(frontLength / 2 - length / 2)
        break

      case ProgramSettings.LabelPositionEnum.LEFT_FRONT_CORNER:
        this.boxGroup.remove(this.labelB)
        this.boxGroup.add(this.labelB)
        left(this.labelA)
        front(this.labelB)
        this.labelA.position.x = -(sideLength / 2 - length / 2)
        this.labelB.position.y = -(frontLength / 2 - length / 2)
        break

      case ProgramSettings.LabelPositionEnum.BACK_LEFT_CORNER:
        this.boxGroup.remove(this.labelB)
        this.boxGroup.add(this.labelB)
        left(this.labelA)
        back(this.labelB)
        this.labelA.position.x = +(sideLength / 2 - length / 2)
        this.labelB.position.y = -(frontLength / 2 - length / 2)
        break
    }
    this.render()
  }

  setBoxOrient (orient: ProgramSettings.BoxOrientationOnConveyorEnum) {
    switch (orient) {
      case ProgramSettings.BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING:
        this.box.setShortSideLeading()
        this.boxObject.rotation.z = 0
        break
      case ProgramSettings.BoxOrientationOnConveyorEnum.LONG_SIDE_LEADING:
        this.box.setLongSideLeading()
        this.boxObject.rotation.z = -Math.PI / 2
        break
    }
    this.setBoxPosition(this.robotPosConfig)
    this.setLabel(this.labelPos)
    this.render()
  }

  rotate2 () {
    const angles = [60, 120, 60, 120]
    this.angle = (this.angle + 1) % 4
    const r = angles[this.angle] / 180 * Math.PI
    // console.log('[DEBUG] r, this.angle', r, this.angle)
    this.scene.rotateZ(r)
    this.renderer.render(this.scene, this.camera)
  }

  rotate () {
    this.animation = !this.animation
    this.animate()
    // const angle = 30 / 180 * Math.PI
    // this.scene.rotateZ(angle)
    // this.renderer.render(this.scene, this.camera)
  }

  moveScene (c: string, l: string) {
    if (this.animation || !c || !l) {
      return
    }
    const le = ProgramSettings.LabelPositionEnum
    const ce = ConveyorConfigurationEnum
    const angleMap = [
      {k: 'A', a: 10, v: (l === le.FRONT || l === le.LEFT || l === le.LEFT_FRONT_CORNER || l === le.IGNORE) && !this.isCustom},
      {k: 'B', a: 95, v: l === le.FRONT_RIGHT_CORNER},
      {k: 'C', a: 310, v: l === le.BACK_LEFT_CORNER},
      {k: 'D', a: 190, v: l === le.BACK || l === le.RIGHT || l === le.RIGHT_BACK_CORNER},
      {k: 'E', a: 90, v: this.isCustom}
    ]

    // set rotation
    const found1 = angleMap.find(p => p.v)
    const angle = found1 ? found1 : angleMap[0]
    const posA = angle.a

    const heightMap = [
      {k: 'C11', z: 550, v: c === ce.LEFT && angle.k === 'A'},
      {k: 'C12', z: 700, v: c === ce.LEFT && angle.k === 'B'},
      {k: 'C13', z: 540, v: c === ce.LEFT && angle.k === 'C'},
      {k: 'C14', z: 750, v: c === ce.LEFT && angle.k === 'D'},
      {k: 'C21', z: 1050, v: (c === ce.CENTER_RIGHT || c === ce.CENTER_LEFT) && angle.k === 'A'},
      {k: 'C23', z: 730, v: (c === ce.CENTER_RIGHT || c === ce.CENTER_LEFT) && angle.k === 'B'},
      {k: 'C23', z: 730, v: (c === ce.CENTER_RIGHT || c === ce.CENTER_LEFT) && angle.k === 'C'},
      {k: 'C22', z: 350, v: (c === ce.CENTER_RIGHT || c === ce.CENTER_LEFT) && angle.k === 'D'},
      {k: 'C41', z: 1100, v: (c === ce.RIGHT) && angle.k === 'A'},
      {k: 'C42', z: 550, v: (c === ce.RIGHT) && angle.k === 'B'},
      {k: 'C43', z: 1020, v: (c === ce.RIGHT) && angle.k === 'C'},
      {k: 'C44', z: 300, v: (c === ce.RIGHT) && angle.k === 'D'},
      {k: 'C45', z: 850, v: (c === ce.CENTER_RIGHT || c === ce.CENTER_LEFT) && angle.k === 'E'},
    ]

    // set height
    const found2 = heightMap.find(p => p.v)
    const height = found2 ? found2 : heightMap[0]
    const posZ = height.z
    this.cameraReset(this.camera)
    this.scene.setRotationFromAxisAngle(new THREE.Vector3(0, 0, 1), posA / 180 * Math.PI)
    this.group.position.z = posZ
    if (this.isCustom) {
      // move (conveyor) group to "center" and rotate
      this.group.position.x = this.viewPort.w / 4
      this.group.position.y = 0
      this.group.rotateZ(Math.PI / 2)
    }
    this.render()
  }

  setCustom(isCustom: boolean) {
    this.isCustom = isCustom
    this.boxGroup.remove(this.labelA, this.labelB)
    this.labelA = this.makeLabel(isCustom)
    this.labelB = this.makeLabel(isCustom)
    this.boxGroup.add(this.labelA)
    this.robotObject!.visible = !isCustom
    this.palletA_Object!.visible = !isCustom
    this.palletB_Object!.visible = !isCustom
    this.controls!.enabled = !isCustom
    this.refGroup!.visible = isCustom
  }

  /* ------------------------------------------------------------------------------------------------------------
   *
   * PRIVATE METHODS
   *
   *------------------------------------------------------------------------------------------------------------*/
  private animate() {
    if (!this.animation) {
      return
    }
    const angle = 1 / 180 * Math.PI
    this.scene.rotateZ(angle)
    this.render()
    setTimeout(() => {
        this.animate()
      },
      30
    )
  }

  private sceneSetup (): THREE.Scene {
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(this.background)
    scene.add(new THREE.AmbientLight(0xffffff))
    return scene
  }

  private cameraSetup (): THREE.OrthographicCamera {
    const aspect = this.viewPort.w / this.viewPort.h
    const d = (this.viewPort.w > 550) ? 2 : 10

    const palletHypo = Math.floor((1000 ** 2 + 1000 ** 2) ** (1 / 2))
    const scale = Math.max(defWidth / this.viewPort.w, defHeight / this.viewPort.h)
    const viewSize =  2000 * (1 + 0.3 * (1280 / palletHypo - 1)) * scale

    const camera = new THREE.OrthographicCamera(
      -aspect * viewSize / d,
      aspect * viewSize / d,
      viewSize / d,
      -viewSize / d,
      1,
      10000)

    this.cameraReset(camera)
    return camera
  }

  private cameraReset (camera: THREE.OrthographicCamera) {
    const xPos = (this.isCustom) ? 0 : -700
    const yPos = (this.isCustom) ? 0 : -2000
    camera.up = new THREE.Vector3(0, 0, 1)
    camera.position.set(
      xPos,
      yPos,
      1200
    )
    camera.lookAt(this.scene.position)
  }

  private controlsSetup () {
    const controls = new OrbitControls(this.camera, this.renderer.domElement)
    controls.addEventListener('change', () => { this.render() }) // call this only in static scenes (i.e., if there is no animation loop)
    controls.enableDamping = false // true // an animation loop is required when either damping or auto-rotation are enabled
    controls.dampingFactor = 0.25
    controls.screenSpacePanning = false
    controls.minDistance = 100
    controls.maxDistance = 10000
    controls.maxPolarAngle = Math.PI / 2
    return controls
  }

  private lightSetup () {
    const light = new THREE.SpotLight(0xffffff, 1.1)
    light.position.set(-5000, -3000, 10000)
    light.angle = Math.PI
    light.castShadow = true
    light.shadow.camera.near = 2000
    light.shadow.camera.far = 5000
    light.shadow.mapSize.width = 1024
    light.shadow.mapSize.height = 1024
    this.scene.add(light)
  }

  private renderSetup (): THREE.WebGLRenderer {
    const renderer = new THREE.WebGLRenderer({ antialias: true })
    renderer.setPixelRatio(window.devicePixelRatio)
    renderer.setSize(this.viewPort.w, this.viewPort.h)
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFShadowMap
    this.container.appendChild(renderer.domElement)
    renderer.render(this.scene, this.camera)
    // console.log('[DEBUG] WebGL render created')
    return renderer
  }

  private boxSetup(): THREE.Material[] {
    const boxTexture = new THREE.TextureLoader()
      .load(`${basePath}img/box.jpg`, () => this.renderer.render(this.scene, this.camera))

    const boxTopTexture = new THREE.TextureLoader()
      .load(`${basePath}img/box-top.jpg`, () => this.renderer.render(this.scene, this.camera))

    // const boxLabeledTexture = new THREE.TextureLoader()
    //  .load(`${basePath}img/box-labeled.jpg`, () => this.renderer.render(this.scene, this.camera))

    const emptySide = new THREE.MeshStandardMaterial({ map: boxTexture, roughness: 1 })
    const topSide = new THREE.MeshStandardMaterial({ map: boxTopTexture, roughness: 1 })
    // const labeledSide = new THREE.MeshStandardMaterial({ map: boxLabeledTexture, roughness: 1 })

    return [ emptySide, emptySide, emptySide, emptySide, topSide, emptySide ]
  }

  // noinspection JSUnusedLocalSymbols
  private addHelpers () {
    // base surface
    const L = 1000
    const W = 1000
    const base = new THREE.PlaneGeometry(L, W)
    const obj = new THREE.Mesh(base, new THREE.MeshLambertMaterial({ color: 0x777777 }))
    obj.position.x = L / 2
    obj.position.y = W / 2
    // this.scene.add(obj)

    // axes
    const axesHelper = new THREE.AxesHelper(2000)
    this.scene.add(axesHelper)

    // cube
    // this.makeCube()
  }

  // noinspection JSUnusedLocalSymbols
  private makeCube() {
    const geometry = new THREE.BoxGeometry(500, 500, 500)
    const material = new THREE.MeshNormalMaterial()
    const mesh = new THREE.Mesh(geometry, material)
    this.group.add(mesh)
    this.render()
  }

  // noinspection JSUnusedLocalSymbols
  private makeReferenceFrame () {
    this.refGroup = new THREE.Group()
    const material = new THREE.MeshBasicMaterial({ color: 0xff0066 })
    const line = new THREE.Shape()
    line.lineTo(0, 580)
    line.lineTo(-10, 580)
    line.lineTo(5, 600)
    line.lineTo(20, 580)
    line.lineTo(10, 580)
    line.lineTo(10, 0)
    const yAxis = new THREE.Mesh(new THREE.ShapeGeometry(line), material)

    const line2 = new THREE.Shape()
    line2.lineTo(0, 10)
    line2.lineTo(580, 10)
    line2.lineTo(580, 20)
    line2.lineTo(600, 5)
    line2.lineTo(580, -10)
    line2.lineTo(580, 0)
    const xAxis = new THREE.Mesh(new THREE.ShapeGeometry(line2), material)

    const fontSize = 50
    const textData = [
      { x: 0, y: 605 + fontSize, displayText: 'y' },
      { x: 605 + fontSize * 0.5, y: fontSize * 0.5, displayText: 'x' }
    ]
    this.addText(textData, fontSize, this.refGroup)
    this.refGroup.add(yAxis, xAxis)
    this.refGroup.position.z = 161 // beam position.z + depth + 1
    this.refGroup.visible = this.isCustom

    this.group.add(this.refGroup)
  }

  private addText (data: Array<{x: number, y: number, displayText: string}>, size: number, group: THREE.Group) {
    const material = new THREE.LineBasicMaterial({ color: 0xff0066, side: THREE.DoubleSide })
    const loader = new THREE.FontLoader()
    loader.load('helvetiker_regular.typeface.json', (font) => {
      this.font = font
      data.forEach(d => {
        const shapes = this.font!.generateShapes(d.displayText.toString(), size, 5)
        const text = new THREE.Mesh(new THREE.ShapeBufferGeometry(shapes), material)
        text.rotateZ(1.5 * Math.PI) // 270 deg
        text.position.x = d.x
        text.position.y = d.y
        group.add(text)
      })

      this.renderer.render(this.scene, this.camera)
    })
  }

  private setRefPosition (pos: Position) {
    if (this.refGroup) {
      this.refGroup.position.y = pos.ref.y
    }
  }

  private makeConveyor (): THREE.Group {
    const b = { l: conveyorLength, w: 40, h: 160, color: 0xb0bbbb }
    const p = { l: conveyorLength, w: 400, h: 80, color: 0xd9d9d0 }
    const beam = new THREE.BoxBufferGeometry(b.l, b.w, b.h)
    const plank = new THREE.BoxBufferGeometry(p.l, p.w, p.h)

    const beam1 = new THREE.Mesh(beam, new THREE.MeshStandardMaterial({ color: b.color, roughness: 1 }))
    beam1.position.x = b.l / 2
    beam1.position.y = b.w / 2
    beam1.position.z = b.h / 2

    const beam2 = new THREE.Mesh(beam, new THREE.MeshStandardMaterial({ color: b.color, roughness: 1 }))
    beam2.position.x = b.l / 2
    beam2.position.y = b.w / 2 + p.w + b.w
    beam2.position.z = b.h / 2

    const plank1 = new THREE.Mesh(plank, new THREE.MeshStandardMaterial({ color: p.color, roughness: 1 }))
    plank1.position.x = p.l / 2
    plank1.position.y = p.w / 2 + b.w
    plank1.position.z = p.h / 2 + 20

    // make arrow
    const arrow = this.makeArrow()
    arrow.position.x = p.l - 40
    arrow.position.y = p.w / 2 + b.w - 26
    arrow.position.z = plank1.position.z + p.h / 2 + 1
    arrow.rotation.z = Math.PI / 2

    const group = new THREE.Group()
    group.add(beam1, beam2, plank1, arrow)

    this.group.add(group)
    return group
  }

  // noinspection JSMethodCanBeStatic
  private makeArrow (): THREE.Object3D {
    const arrow = new THREE.Shape()
    arrow.lineTo(0, 170)
    arrow.lineTo(-20, 170)
    arrow.lineTo(26, 280)
    arrow.lineTo(70, 170)
    arrow.lineTo(50, 170)
    arrow.lineTo(50, 0)
    const geometry = new THREE.ShapeGeometry(arrow)
    const material = new THREE.MeshBasicMaterial({ color: 0x777777 })
    return new THREE.Mesh(geometry, material)
  }

  // noinspection JSUnusedLocalSymbols
  private makeBox (box: Box2, materials: THREE.Material[]): {obj: THREE.Object3D, group: THREE.Group} {
    const b: Box2 = new Box2({ length: 300, width: 200, height: 170 })
    box = box ? box : b

    const geometry = new THREE.BoxBufferGeometry(box.sideLength, box.frontLength, box.height)
    const object = new THREE.Mesh(geometry, this.boxMaterials)
    object.castShadow = true
    object.receiveShadow = true

    const group = new THREE.Group()
    group.add(object, this.labelA)
    group.position.x = 420 / 2
    group.position.y = 40 + box.frontLength / 2
    group.position.z = 20 + 80 + box.height / 2

    // this.scene.add(group)
    this.group.add(group)
    return {obj: object, group: group}
  }

  private setBoxPosition (pos: Position) {
    const offset = (this.isCustom && 'CENTER_RIGHT' === pos.id) ? 45 : 40
    this.boxGroup.position.y = offset + this.box.frontLength / 2 + pos.box.ky * (conveyorWidth - this.box.frontLength)
    this.boxGroup.position.x = (this.isCustom) ? 5 + this.box.sideLength / 2 : pos.box.x
  }

  // noinspection JSMethodCanBeStatic
  private makeLabel (thick = false): THREE.Group {
    const L = 100
    const W = 65
    const geometry = thick ? new THREE.BoxGeometry(L, W, 20) : new THREE.PlaneGeometry(L, W)
    const paper = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide }))
    const group = new THREE.Group()
    group.add(paper)
    if (thick) {
      group.add(new THREE.LineSegments(new THREE.EdgesGeometry(geometry), new THREE.LineBasicMaterial({ color: 0x111111 })))
    }
    group.position.x = L / 2
    group.position.y = W / 2
    group.position.z = 200
    return group
  }

  private importRobot (after?: () => void) {
    // tslint:disable-next-line
    let THREE = require('three')
    const STLLoader = require('three-stl-loader')(THREE)

    const loader = new STLLoader()
    const material = new THREE.MeshStandardMaterial({ color: 0xffa64d, roughness: 0.9 })
    let mesh: THREE.Object3D
    loader.load('robot.stl', (geometry: any) => {
      mesh = new THREE.Mesh(geometry, material)
      mesh.position.set(0, 0, -200)
      mesh.rotation.set(0, 0, Math.PI / 2)
      mesh.scale.set(0.8, 0.8, 0.8)
      mesh.castShadow = true
      mesh.receiveShadow = true
      this.robotObject = mesh
      // this.scene.add(mesh)
      this.group.add(mesh)
      // console.log('[DEBUG] import robot')
      // this.setPosition(ConveyorConfigurationEnum.LEFT)
      this.animate()
      if (after) {
        after()
      }
    })
  }

  private makePallet (): THREE.Group {
    const offset = { x: 0, y: 0, z: -200 }
    const pallet = { length: 800, width: 600, height: 100 }
    const color = 0xc1af7a
    const p = { l: pallet.length!, w: 80, h: 20 }
    const b = { l: pallet.width!, w: pallet.height! - p.h, h: pallet.height! - p.h }

    const plankTexture = new THREE.TextureLoader()
      .load(`${basePath}img/pine.jpg`, () => this.renderer.render(this.scene, this.camera))

    const material = new THREE.MeshStandardMaterial({ map: plankTexture, roughness: 1 })
    // const material = new THREE.MeshStandardMaterial({ color: color, roughness: 1 })

    const beam = new THREE.BoxBufferGeometry(b.l, b.w, b.h)
    const plank = new THREE.BoxBufferGeometry(p.l, p.w, p.h)

    const beam1 = new THREE.Mesh(beam, material)
    beam1.rotateZ(90 / 180 * Math.PI)
    beam1.position.x = b.w / 2
    beam1.position.y = b.l / 2
    beam1.position.z = b.h / 2

    const beam2 = new THREE.Mesh(beam, material)
    beam2.rotateZ(90 / 180 * Math.PI)
    beam2.position.x = pallet.length! / 2
    beam2.position.y = b.l / 2
    beam2.position.z = b.h / 2

    const beam3 = new THREE.Mesh(beam, material)
    beam3.rotateZ(90 / 180 * Math.PI)
    beam3.position.x = pallet.length! - b.w / 2
    beam3.position.y = b.l / 2
    beam3.position.z = b.h / 2

    const plank1 = new THREE.Mesh(plank, material)
    plank1.position.x = p.l / 2
    // noinspection PointlessArithmeticExpressionJS
    plank1.position.y = (pallet.width! - p.w) / 4 * 0 + p.w / 2
    plank1.position.z = b.h + p.h / 2

    const plank2 = new THREE.Mesh(plank, material)
    plank2.position.x = p.l / 2
    // noinspection PointlessArithmeticExpressionJS
    plank2.position.y = (pallet.width! - p.w) / 4 * 1 + p.w / 2
    plank2.position.z = b.h + p.h / 2

    const plank3 = new THREE.Mesh(plank, material)
    plank3.position.x = p.l / 2
    plank3.position.y = (pallet.width! - p.w) / 4 * 2 + p.w / 2
    plank3.position.z = b.h + p.h / 2

    const plank4 = new THREE.Mesh(plank, material)
    plank4.position.x = p.l / 2
    plank4.position.y = (pallet.width! - p.w) / 4 * 3 + p.w / 2
    plank4.position.z = b.h + p.h / 2

    const plank5 = new THREE.Mesh(plank, material)
    plank5.position.x = p.l / 2
    plank5.position.y = (pallet.width! - p.w) / 4 * 4 + p.w / 2
    plank5.position.z = b.h + p.h / 2

    const group = new THREE.Group()
    group.add(beam1, beam2, beam3, plank1, plank2, plank3, plank4, plank5)

    group.position.z += offset.z
    group.rotateZ(Math.PI / 2)
    // this.scene.add(group)
    this.group.add(group)
    return group
  }
}
